import type { DateRange } from "@/features/admin-dashboard/utils/date-ranges"

import { api } from "@/lib/api-client"

import { buildDateRangeQuery } from "./_shared"

export type AdminTopUsersResponse = {
  rangeDays: number
  users: Array<{
    userId: string
    alias: string | null
    email?: string | null
    profile?: {
      name?: string | null
      email?: string | null
      jobTitle?: string | null
      department?: string | null
      officeLocation?: string | null
      company?: string | null
    } | null
    chats: number
    searches: number
    tokens: {
      in: number
      out: number
      total: number
    }
  }>
}

export type AdminRecentEventsResponse = {
  events: Array<{
    timestamp: string
    type: string
    description: string
    actor: string | null
    metadata: Record<string, unknown>
  }>
}

export type AdminUsageResponse = {
  rangeDays: number
  points: Array<{
    date: string
    tokensIn: number
    tokensOut: number
    tokensTotal: number
    requests: number
    errors: number
    costUSD: number
  }>
  summary: {
    tokensIn: number
    tokensOut: number
    tokensTotal: number
    usersActive: number
    p95: number
    errorRate: number
    costUSD: number
  }
}

export async function getTopUsers(dateRange: DateRange | 7 | 30): Promise<AdminTopUsersResponse> {
  // Backward compatibility: convert numeric range to DateRange
  const range =
    typeof dateRange === "number" ? { type: "days" as const, days: dateRange } : dateRange
  const queryString = buildDateRangeQuery(range)
  const response = await api.get(`/admin/users/top?${queryString}`)
  return response.data || response
}

export async function getRecentEvents(limit: number = 10): Promise<AdminRecentEventsResponse> {
  const response = await api.get(`/admin/events/recent?limit=${limit}`)
  return response.data || response
}

export async function getUsage(dateRange: DateRange | 7 | 30): Promise<AdminUsageResponse> {
  // Backward compatibility: convert numeric range to DateRange
  const range =
    typeof dateRange === "number" ? { type: "days" as const, days: dateRange } : dateRange
  const queryString = buildDateRangeQuery(range)
  const response = await api.get(`/admin/usage?${queryString}`)
  return response.data || response
}

export type AdminAgentRunsResponse = {
  months: Array<{
    month: string
    agents: Array<{
      agentId: string
      agentName: string
      runs: number
    }>
  }>
}

export type AdminAgentSummaryResponse = {
  agents: Array<{
    agentId: string
    agentName: string
    runs: number
    successPct: number
    p95: number
    failures: number
  }>
}

export async function getAgentSummary(
  dateRange: DateRange | 7 | 30
): Promise<AdminAgentSummaryResponse> {
  // Backward compatibility: convert numeric range to DateRange
  const range =
    typeof dateRange === "number" ? { type: "days" as const, days: dateRange } : dateRange
  const queryString = buildDateRangeQuery(range)
  const response = await api.get(`/admin/agents/summary?${queryString}`)
  return response.data || response
}

export async function getAgentRuns(
  dateRange: DateRange | 7 | 30 = { type: "days", days: 30 }
): Promise<AdminAgentRunsResponse> {
  // Backward compatibility: convert numeric range to DateRange
  const range =
    typeof dateRange === "number" ? { type: "days" as const, days: dateRange } : dateRange
  const queryString = buildDateRangeQuery(range)
  const response = await api.get(`/admin/agents/runs?${queryString}`)
  return response.data || response
}

export type AdminModelUsageResponse = {
  models: Array<{
    model: string
    calls: number
  }>
}

export async function getModelUsage(
  dateRange: DateRange | 7 | 30
): Promise<AdminModelUsageResponse> {
  // Backward compatibility: convert numeric range to DateRange
  const range =
    typeof dateRange === "number" ? { type: "days" as const, days: dateRange } : dateRange
  const queryString = buildDateRangeQuery(range)
  const response = await api.get(`/admin/model-usage?${queryString}`)
  return response.data || response
}

export type AdminSearchesResponse = {
  rangeDays: number
  points: Array<{
    date: string
    queries: number
  }>
  total: number
  changePct: number
}

export async function getSearches(range: 7 | 30): Promise<AdminSearchesResponse> {
  const response = await api.get(`/admin/searches?range=${range}`)
  return response.data || response
}

export type AdminFeatureAdoptionResponse = {
  session_types: Record<string, number>
  project_sessions: number
  upload_sessions: number
  total_sessions: number
}

export async function getFeatureAdoption(
  dateRange: DateRange | 7 | 30
): Promise<AdminFeatureAdoptionResponse> {
  // Backward compatibility: convert numeric range to DateRange
  const range =
    typeof dateRange === "number" ? { type: "days" as const, days: dateRange } : dateRange
  const queryString = buildDateRangeQuery(range)
  const response = await api.get(`/admin/feature-adoption?${queryString}`)
  return response.data || response
}

export type AdminUploadStatsResponse = {
  total_files: number
  total_size_mb: number
  sessions_with_uploads: number
}

export async function getUploadStats(
  dateRange: DateRange | 7 | 30
): Promise<AdminUploadStatsResponse> {
  // Backward compatibility: convert numeric range to DateRange
  const range =
    typeof dateRange === "number" ? { type: "days" as const, days: dateRange } : dateRange
  const queryString = buildDateRangeQuery(range)
  const response = await api.get(`/admin/upload-stats?${queryString}`)
  return response.data || response
}

export type AdminFeatureUsageStatsResponse = {
  total_messages?: number
  file_picker?: {
    count: number
    percentage: number
    avg_files: number
    total_size_mb: number
  }
  high_reasoning?: {
    count: number
    percentage: number
    effort_levels: Record<string, number>
  }
  web_search?: {
    count: number
    percentage: number
  }
  trusted_sources?: {
    count: number
    percentage: number
    only_mode_count: number
  }
  scholar?: {
    count: number
    with_research_count: number
  }
  prompt_library?: {
    count: number
    top_prompts: Array<{
      id: string
      title: string
      count: number
    }>
  }
  flow?: {
    count: number
    top_workflows: Array<{
      id: string
      count: number
    }>
  }
  activated_libraries?: {
    count: number
    top_libraries: Array<{
      id: string
      count: number
    }>
  }
  tool_approvals?: {
    count: number
    approved: number
    denied: number
    expired: number
    by_tool: Array<{
      tool: string
      count: number
    }>
  }
}

export async function getFeatureUsageStats(
  dateRange: DateRange | 7 | 30
): Promise<AdminFeatureUsageStatsResponse> {
  // Backward compatibility: convert numeric range to DateRange
  const range =
    typeof dateRange === "number" ? { type: "days" as const, days: dateRange } : dateRange
  const queryString = buildDateRangeQuery(range)
  const response = await api.get(`/admin/feature-usage-stats?${queryString}`)
  return response.data || response
}

export type AdminUsageInsightsResponse = {
  total_messages: number
  total_sessions: number
  active_users: number
  known_users: number
  avg_messages_per_session: number
  median_messages_per_session: number
  p90_messages_per_session: number
  avg_llm_calls_per_message: number
  avg_tokens_per_message: number
  avg_tokens_per_session: number
  avg_sessions_per_user: number
  top_users_token_share: number
  top_chats_token_share: number
  daily: Array<{
    date: string
    messages: number
    sessions: number
    active_users: number
    llm_calls: number
    total_tokens: number
    tool_calls: number
  }>
  by_model: Array<{
    model: string
    messages: number
    sessions: number
    llm_calls: number
    total_tokens: number
    calls_per_message: number
    tokens_per_message: number
    tokens_per_session: number
  }>
  tools: Array<{
    tool: string
    calls: number
    messages: number
  }>
}

export async function getUsageInsights(
  dateRange: DateRange | 7 | 30
): Promise<AdminUsageInsightsResponse> {
  // Backward compatibility: convert numeric range to DateRange
  const range =
    typeof dateRange === "number" ? { type: "days" as const, days: dateRange } : dateRange
  const queryString = buildDateRangeQuery(range)
  const response = await api.get(`/admin/usage-insights?${queryString}`)
  return response.data || response
}

export type AdminDashboardSummaryResponse = {
  computedAt?: string
  usage: AdminUsageResponse
  searches: AdminSearchesResponse
  topUsers: AdminTopUsersResponse
}

export async function getDashboardSummary(
  dateRange: DateRange | 7 | 30,
  currency: "SEK" | "USD" | "EUR" = "USD"
): Promise<AdminDashboardSummaryResponse> {
  // Backward compatibility: convert numeric range to DateRange
  const range =
    typeof dateRange === "number" ? { type: "days" as const, days: dateRange } : dateRange
  const queryString = buildDateRangeQuery(range)
  const response = await api.get(`/admin/dashboard-summary?${queryString}&currency=${currency}`)
  return response.data || response
}
